import { supabase } from './supabase.js'
import { todayISO } from './format.js'

/**
 * Weight log for the Health category. A goal, a timeline, and entries by date.
 *
 * One rule shapes the whole thing: **show the trend, not a daily verdict.**
 * Weight swings a kilo or two from water, salt and time of day. Marking a
 * single day good or bad is reading noise, and it is the part of these apps
 * that makes people feel worse rather than better. So the app draws the line
 * and the seven-day average, and says nothing about any one number.
 */

export const UNITS = ['kg', 'lb']

/** One entry per day, so logging twice corrects rather than duplicates. */
export async function logWeight({ weight, on, note }) {
  const { data, error } = await supabase
    .from('weight_logs')
    .upsert(
      { logged_on: on || todayISO(), weight, note: note?.trim() || null },
      { onConflict: 'user_id,logged_on' },
    )
    .select()
    .single()
  if (error) throw error

  try {
    await supabase.from('activity_log').insert({
      kind: 'weight.log',
      summary: 'Weight logged',
      target_type: 'weight_log',
      target_id: data.id,
    })
  } catch {
    /* the log is never load-bearing */
  }
  return data
}

export async function deleteLog(id) {
  const { error } = await supabase.from('weight_logs').delete().eq('id', id)
  if (error) throw error
}

export async function getLogs(limit = 180) {
  const { data, error } = await supabase
    .from('weight_logs')
    .select('*')
    .order('logged_on', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).reverse()
}

export async function getGoal() {
  const { data, error } = await supabase
    .from('weight_goals')
    .select('*')
    .is('achieved_at', null)
    .order('created_at', { ascending: false })
    .limit(1)
  if (error) throw error
  return data?.[0] ?? null
}

export async function setGoal(goal) {
  const { data, error } = await supabase
    .from('weight_goals')
    .insert({
      unit: goal.unit || 'kg',
      start_weight: goal.startWeight ?? null,
      target_weight: goal.targetWeight,
      start_on: goal.startOn || todayISO(),
      target_on: goal.targetOn || null,
      note: goal.note?.trim() || null,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function clearGoal(id) {
  const { error } = await supabase.from('weight_goals').delete().eq('id', id)
  if (error) throw error
}

/**
 * A trailing seven-day average over the logged points. This is the number
 * worth looking at — the raw line is mostly noise, and the average is what
 * actually moves.
 */
export function smooth(logs, days = 7) {
  return logs.map((l, i) => {
    const from = new Date(l.logged_on)
    from.setDate(from.getDate() - (days - 1))
    const window = logs.slice(0, i + 1).filter((x) => new Date(x.logged_on) >= from)
    const avg = window.reduce((s, x) => s + Number(x.weight), 0) / window.length
    return { ...l, avg }
  })
}

/**
 * Where things stand. Deliberately arithmetic and nothing else: no
 * encouragement, no warning, no "on track". He can read a number.
 */
export function progress(logs, goal) {
  if (!logs.length) return null

  const latest = logs[logs.length - 1]
  const now = Number(latest.weight)
  const points = smooth(logs)
  const avgNow = points[points.length - 1].avg

  const first = logs[0]
  const start = goal?.start_weight != null ? Number(goal.start_weight) : Number(first.weight)
  const changed = now - start

  let toGo = null
  let pct = null
  if (goal) {
    const target = Number(goal.target_weight)
    toGo = now - target
    const span = start - target
    /* A goal already met, or one set at the current weight, has no meaningful
       percentage — better to show nothing than a divide by zero dressed up. */
    if (Math.abs(span) > 0.01) {
      pct = Math.max(0, Math.min(100, Math.round(((start - now) / span) * 100)))
    }
  }

  /* Rate over the last four weeks of actual entries, not calendar guesswork. */
  const monthAgo = new Date(latest.logged_on)
  monthAgo.setDate(monthAgo.getDate() - 28)
  const recent = logs.filter((l) => new Date(l.logged_on) >= monthAgo)
  let perWeek = null
  if (recent.length >= 2) {
    const spanDays =
      (new Date(recent[recent.length - 1].logged_on) - new Date(recent[0].logged_on)) / 86400000
    if (spanDays >= 3) {
      const delta = Number(recent[recent.length - 1].weight) - Number(recent[0].weight)
      perWeek = (delta / spanDays) * 7
    }
  }

  return {
    now,
    avgNow,
    start,
    changed,
    toGo,
    pct,
    perWeek,
    on: latest.logged_on,
    count: logs.length,
    unit: goal?.unit || 'kg',
  }
}
